import * as React from 'react';
import { useEffect } from 'react';
import styled from 'styled-components';
import { ArticleList, ArticleTypeListing, rng, stringUtil } from '../../../amorg-common';
import { articlesClientApi } from '../../../api/articlesClientApi';
import { Button } from '../../../designSystem/button/Button';
import { ButtonRow } from '../../../designSystem/button/ButtonRow';
import { EditableText } from '../../../designSystem/compositeComponents/EditableText';
import { Flex } from '../../../designSystem/compositeComponents/FlexHelpers';
import { ListRow } from '../../../designSystem/compositeComponents/ListRow';
import { Icon } from '../../../designSystem/icons/Icons';
import { LoadingPageOverlay } from '../../../designSystem/LoadingPageOverlay';
import { P, Small } from '../../../designSystem/typography/Body';
import { H1, H2 } from '../../../designSystem/typography/Heading';
import { ArticlesTab } from './_components/ArticlesTab';
import { AmorgyPageWrapper } from '../../_pageWrappers/AmorgyPageWrapper';
import { SingleColumnLayout } from '../../_layouts/SingleColumnLayout';



export default function ArticlesConfiguration (): JSX.Element 
{
    const [loading, setLoading] = React.useState(true);
    const [lists, setLists] = React.useState<ArticleList[]>([]);
    const [types, setTypes] = React.useState<ArticleTypeListing[]>([]);


    useEffect(() =>
    {
        document.title = 'Amorgy - Lovkrav opsætning';
        const init = async () =>
        {
            setLists(await articlesClientApi.getLists());
            setTypes(await articlesClientApi.getTypes());
            setLoading(false);
        };
        init().catch(console.error);
    }, []);

    async function handleTypesChanged ()
    {
        setTypes(await articlesClientApi.getTypes());
    }

    async function handleListsChanged ()
    {
        setLists(await articlesClientApi.getLists());
    }

    return (
        <AmorgyPageWrapper selectedTab='articles'
            subMenuContent={
                <ArticlesTab selectedTab='configuration' />
            }>
            <LoadingPageOverlay loading={loading} />
            <SingleColumnLayout narrow>

                    <Flex flexDirection='column' gap={16}>

                        <H1>Opsætning</H1>

                        <TypeList types={types} onChanged={handleTypesChanged} />

                        <ListsList lists={lists} onChanged={handleListsChanged} />

                    </Flex>
            </SingleColumnLayout>

        </AmorgyPageWrapper>
    );
}

type TypeList = {
    types?: ArticleTypeListing[],
    onChanged: () => void;
};

function TypeList (props: TypeList): JSX.Element 
{
    async function handleClickCreateType ()
    {
        const types = await articlesClientApi.getTypes();
        const name = stringUtil.generateNextSequenceValue('Ny type', types.map(t => t.name));
        await articlesClientApi.createType(rng.getRandomId(), name);
        props.onChanged();
    }

    return (
        <Flex flexDirection='column'>
            <ListRow
                onCollapse={() => { return; }}
                styleVariant="outlined"
                content={
                    <Flex justifyContent="space-between" alignItems='center'>
                        <Flex alignItems="center" gap={16}>
                            <Icon name="facility_serverRoom" /><H2>Lovtyper</H2>
                        </Flex>
                        <Small>{props.types?.length || 0} typer</Small>
                    </Flex>
                }
                childRows={
                    <ListContainer>
                        {props.types?.map(t =>
                            <TypeRow key={t.id} {...t} onChanged={props.onChanged} />
                        )}
                        <ButtonRow marginTop={8} marginRight={8} marginBottom={8} variant='right-aligned'>
                            <Button icon="actions_plus" variant='filled' text='Opret lovtype' onClick={handleClickCreateType} />
                        </ButtonRow>

                    </ListContainer>
                }
            />
        </Flex>

    );
}

type TypeRowProps = {
    id: string,
    name: string,
    articleCount: number,
    onChanged: () => void;
};
function TypeRow (props: TypeRowProps): JSX.Element
{
    const [error, setError] = React.useState('');

    async function handleUpdate (value: string)
    {
        const result = await articlesClientApi.updateType(props.id, value);
        if (result.status === 'success')
        {
            props.onChanged();
            setError('');
            return true;
        }
        else
        {
            if (result.clientMessage === 'invalid name format')
                setError('Ugyldigt navn');
            else if (result.clientMessage === 'name in use')
                setError('Navn i brug');
            else
                setError('Ukendt fejl');
            return false;
        }
    }

    async function handleDelete ()
    {
        await articlesClientApi.deleteType(props.id);
        props.onChanged();
    }

    return (
        <ListRow
            key={props.id}
            styleVariant={props.articleCount > 0 ? 'outlined' : 'lightGray'}
            content={
                <Flex justifyContent={"space-between"} alignItems='center'>

                <Flex gap={16} alignItems='center'>
                    <P><EditableText styleMode="default" ariaLabel='typenavn' placeholder='' value={props.name} onSaveWithReturnValue={handleUpdate} inErrorState={error !== ''} /></P>
                    {error &&
                        <Small color="error">{error}</Small>
                    }
                </Flex>

                <Flex gap={8} alignItems='center'>
                    <Small>(Brugt af {props.articleCount} lovkrav)</Small>
                    <Button icon="interface_actions_delete" variant="destructive-text" toolTipText="Slet" disabled={props.articleCount > 0} onClick={handleDelete} />
                </Flex>
                </Flex>
            } />
    );
}



type ListsListProps = {
    lists?: ArticleList[],
    onChanged: () => void;
};

function ListsList (props: ListsListProps): JSX.Element 
{
    async function handleClickCreateList ()
    {
        const lists = await articlesClientApi.getLists();
        const name = stringUtil.generateNextSequenceValue('Ny lovkravsliste', lists.map(l => l.name));
        await articlesClientApi.createList(rng.getRandomId(), name);
        props.onChanged();
    }

    return (
        <Flex flexDirection='column'>
            <ListRow
                onCollapse={() => { return; }}
                styleVariant="outlined"

                content={
                    <Flex justifyContent="space-between" alignItems='center'>
                        <Flex alignItems="center" gap={8}>
                            <Icon name="list_alt" /><H2>Lovkravslister</H2>
                        </Flex>

                        <Small>{props.lists?.length || 0} lister</Small>

                    </Flex>
                }
                childRows={
                    <ListContainer>
                        {props.lists?.map(l =>
                            <ArticleListRow key={l.id} list={l} onChanged={props.onChanged} />
                        )}

                        <ButtonRow marginTop={8} marginRight={8} marginBottom={8} variant='right-aligned'>
                            <Button icon="actions_plus" text="Opret liste" variant="filled" onClick={handleClickCreateList} />
                        </ButtonRow>

                    </ListContainer>
                }
            />
        </Flex>
    );
}

type ArticleListRowProps = {
    list: ArticleList,
    onChanged: () => void;
};

function ArticleListRow (props: ArticleListRowProps): JSX.Element 
{
    const [expanded, setExpanded] = React.useState(false);
    const [error, setError] = React.useState('');

    async function handleUpdate (value: string)
    {
        const result = await articlesClientApi.updateList(props.list.id, value);
        if (result.status === 'success')
        {
            props.onChanged();
            setError('');
            return true;
        }
        else
        {
            if (result.clientMessage === 'invalid name format')
                setError('Ugyldigt navn');
            else if (result.clientMessage === 'list name in use')
                setError('Navn i brug');
            else
                setError('Ukendt fejl');
            return false;
        }
    }

    async function handleDelete ()
    {
        await articlesClientApi.deleteList(props.list.id);
        props.onChanged();
    }

    async function handleClickCreateTopic ()
    {
        const lists = await articlesClientApi.getLists();
        const name = stringUtil.generateNextSequenceValue('Nyt emne', lists.flatMap(l => l.topics.map(t => t.name)));
        await articlesClientApi.createListTopic(props.list.id, rng.getRandomId(), name);
        props.onChanged();
        setExpanded(true);
    }

    const articleCount = props.list.topics.reduce((acc, t) => acc + t.articleCount, 0);

    return (


        <Flex flexDirection='column'>
            <ListRow
                onCollapse={(collapsed) => { setExpanded(!collapsed); }}
                styleVariant="outlined"
                collapsed={!expanded}
                content={
                    <Flex justifyContent={"space-between"} alignItems='center'>
                        <Flex alignItems='center' gap={8} justifyContent='flex-start'>

                            <Small>#{props.list.id}</Small>

                            <P><EditableText styleMode="default" ariaLabel='listenavn' placeholder='' value={props.list.name} onSaveWithReturnValue={handleUpdate} inErrorState={error !== ''} /></P>

                            {error &&
                                <Small color="error">{error}</Small>
                            }

                        </Flex>

                        <Flex gap={16}>
                            <Flex gap={8}>
                                <Button
                                    icon="interface_actions_delete"
                                    variant="destructive-text"
                                    toolTipText="Slet"
                                    disabled={articleCount > 0 || props.list.topics.length > 0}
                                    onClick={handleDelete} />
                                <Button
                                    icon="add_circle"
                                    variant="text"
                                    toolTipText="Opret emne"
                                    onClick={handleClickCreateTopic} />
                            </Flex>
                        </Flex>
                    </Flex>
                }

                childRows={
                    props.list.topics.map(t =>
                        <TopicRow key={t.id} {...t} onChanged={props.onChanged} />
                    )
                }
            />
        </Flex>
    );
}

type TopicRowProps = {
    id: string,
    name: string,
    articleCount: number,
    onChanged: () => void;
};
function TopicRow (props: TopicRowProps): JSX.Element
{
    const [error, setError] = React.useState('');

    async function handleUpdate (value: string)
    {
        const result = await articlesClientApi.updateListTopic(props.id, value);
        if (result.status === 'success')
        {
            props.onChanged();
            setError('');
            return true;
        }
        else
        {
            if (result.clientMessage === 'invalid name format')
                setError('Ugyldigt navn');
            else if (result.clientMessage === 'name in use')
                setError('Navn i brug');
            else
                setError('Ukendt fejl');
            return false;
        }
    }

    async function handleDelete ()
    {
        await articlesClientApi.deleteListTopic(props.id);
        props.onChanged();
    }

    return (
        <ListRow key={props.id} styleVariant='lightGray'
            content={

        <Flex justifyContent={"space-between"} alignItems='center'>

                <Flex gap={16} alignItems='center'>
                    <P><EditableText styleMode="default" ariaLabel='emnenavn' placeholder='' value={props.name} onSaveWithReturnValue={handleUpdate} inErrorState={error !== ''} /></P>
                    {error &&
                        <Small color='error'>{error}</Small>
                    }
                </Flex>
                <Flex gap={16} alignItems='center'>
                    <Small>(Brugt af {props.articleCount} lovkrav)</Small>
                    <ButtonRow marginRight={88}>
                        <Button icon="interface_actions_delete" variant="destructive-text" toolTipText="Slet"
                            disabled={props.articleCount > 0}
                            onClick={handleDelete}
                        />

                    </ButtonRow>
                </Flex>
                </Flex>
            } />
    );
}

const ListContainer = styled.div`
    border: 1px solid black;
`;